import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { KaSelect, KaSelectOption, toKaSelectOptions } from '../Common/KaSelect';
import SpinnerButton from '../Common/SpinnerButton';
import BoldButton from '../Common/BoldButton';
import useGenerateDataSharingCode from '../../requests/useGenerateDataSharingCode';
import { DataSharingSiteDto } from '../../requests/useGetDataSharingConnections';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';

interface IProps {
  sites: DataSharingSiteDto[];
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const fullLink = (code: string) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/dataSharing/acceptConnection/${code}`;
}

export const GenerateConnectionCodeModal = ({ sites, show, onClose, onSuccess }: IProps) => {
  const displayMessage = useDisplayMessage();
  const [selectedSite, setSelectedSite] = useState<KaSelectOption | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | undefined>(undefined);

  const generateCode = useGenerateDataSharingCode({
    onSuccess: (response) => {
      const link = fullLink(response.code);
      setGeneratedLink(link);
      navigator.clipboard.writeText(link);
    },
    onError: (err) => {
      displayMessage.fail(err.message);
    }
  });

  const handleGenerateCode = () => {
    if (selectedSite) {
      generateCode.request({ siteId: selectedSite.value });
    }
  };

  const handleClose = () => {
    setSelectedSite(null);
    onClose();
  };

  const handleCloseSuccessModal = () => {
    setGeneratedLink(undefined);
    onSuccess();
  };

  return <>
    <Modal show={show && !generatedLink} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Generate Connection Code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <Form.Group className="mb-3">
          <Form.Label>Select Site</Form.Label>
          <KaSelect
            placeholder="Choose a site..."
            options={toKaSelectOptions(sites ?? [], "siteId", "siteName", { sortByLabel: true })}
            value={selectedSite}
            onChange={(option) => setSelectedSite(option as KaSelectOption | null)}
            isClearable
            isMulti={false}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <BoldButton
          className="btn-ghost-secondary"
          onClick={handleClose}
        >
          Cancel
        </BoldButton>
        <SpinnerButton
          className="btn-ghost-primary"
          onClick={handleGenerateCode}
          disabled={!selectedSite}
          spinning={generateCode.isLoading}
        >
          Generate Code
        </SpinnerButton>
      </Modal.Footer>
    </Modal>
    <Modal
      show={!!generatedLink}
      onHide={handleCloseSuccessModal}
      size="lg"
    >
      <Modal.Header closeButton className="border-0">
        <div className="d-flex align-items-center">
          <i className="bi bi-check-circle-fill text-success me-2" style={{ fontSize: '2rem' }}></i>
          <h3 className="mb-0">Link copied</h3>
        </div>
      </Modal.Header>
      <Modal.Body className="p-4 pt-0">
        <div className="bg-light p-3 rounded mb-4">
          <p className="mb-2"><strong>Your connection code:</strong></p>
          <code className="fs-5">{generatedLink}</code>
        </div>
        <div className="text-muted">
          <p>
            I acknowledge and agree that by sharing my data I am granting access to a third party at my discretion. I am responsible for reviewing
            and managing shared connections appropriately. Kahler Automation is not responsible for how the third party uses or
            manages shared data and I will ensure compliance with any applicable data privacy and security policies before proceeding.
          </p>
          <p>
            I understand that revoking access will remove the third party's visibility to my data. I acknowledge the importance of carefully
            managing data-sharing connections including initiating and terminating access.
          </p>
        </div>
        <div className="text-end mt-4">
          <BoldButton
            className="btn-ghost-primary px-5"
            onClick={handleCloseSuccessModal}>
            Done
          </BoldButton>
        </div>
      </Modal.Body>
    </Modal>
  </>
};